import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthUserContext } from '../Session';
import '../Dashboard/styles.css';
import './franchiseDashboardDemo.css';

const DASHBOARD_URL = '/dashboard?view=franchise';
const ACCESS_URL = '/dashboard?view=franchise&focus=phone';
const PRODUCT_URL = '/product/franchise';
const ACTIVE_NAV_STYLE = {
  background: 'rgba(168, 168, 168, 0.12)',
  color: '#f8f8f8',
};
const INACTIVE_NAV_STYLE = {
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.65)',
};

const SECTION_LABELS = {
  home: 'Home',
  'leads-crm': 'Leads / CRM',
  'leads-request': 'Leads / Request Lead/Listing',
  'branding-overview': 'Branding & Marketing Suite / Overview',
  'branding-brand-kit': 'Branding & Marketing Suite / Brand Kit',
  'branding-printables': 'Branding & Marketing Suite / Printables',
  'branding-studio': 'Branding & Marketing Suite / Studio (Beta)',
  'branding-assets': 'Branding & Marketing Suite / Assets',
  'academy-training-online': 'Academy / Training / Online Training',
  'academy-training-onsite': 'Academy / Training / On site training',
  'academy-study': 'Academy / Study Material',
  'exclusive-mandates': 'Exclusive Mandates',
  events: 'Events',
  templates: 'Templates',
  support: 'Support',
};

const SECTION_CONTENT = {
  home: {
    heading: 'Home / Franchise Dashboard',
    description: 'See your franchise progress, key updates, and next actions in one clean view.',
    features: [
      'Your profile and franchise details in one place',
      'Quick status view for onboarding and approvals',
      'Easy access to franchise card and agreement',
      'Important alerts and updates at the top',
      'Fast entry points to core business sections',
    ],
  },
  'leads-crm': {
    heading: 'Leads / CRM',
    description: 'Stay on top of every opportunity with a clear lead pipeline and follow-up workflow.',
    features: [
      'Lead pipeline view from new inquiry to closure',
      'Lead and client records with complete context',
      'Follow-up scheduling to keep opportunities active',
      'Performance snapshots for smarter daily decisions',
      'Includes 3 CRM user accounts for 5 years',
    ],
  },
  'leads-request': {
    heading: 'Leads / Request Lead & Listing',
    description: 'Submit lead and listing requirements quickly so your pipeline keeps moving.',
    features: [
      'Simple request form for lead requirements',
      'Listing request submission from one section',
      'Clear format to share your priorities',
      'Request history and status visibility',
    ],
  },
  'branding-overview': {
    heading: 'Branding / Overview',
    description: 'Access all branding tools from one section so your business always looks consistent and premium.',
    features: [
      'Central hub for Brand Kit, Printables, Studio, and Assets',
      'Quick navigation to each branding tool',
      'Consistent identity across every customer touchpoint',
      'Faster campaign and collateral readiness',
    ],
  },
  'branding-brand-kit': {
    heading: 'Branding / Brand Kit',
    description: 'Use approved brand elements to keep your communication professional and consistent.',
    features: [
      'Official logo, color, and typography references',
      'Brand usage guidance for day-to-day communication',
      'Ready reference for online and offline branding',
      'Confidence that every design matches brand standards',
    ],
  },
  'branding-printables': {
    heading: 'Branding / Printables',
    description: 'Create ready-to-use materials for client meetings, outreach, and local marketing.',
    features: [
      'Business cards and letterheads',
      'Email signature and profile creatives',
      'Print-ready exports for daily operations',
      'Marketing collateral formatted for quick sharing',
      'Single place to prepare client-facing documents',
    ],
  },
  'branding-studio': {
    heading: 'Branding / Studio (Beta)',
    description: 'Design custom creatives quickly with an editor built for daily marketing needs.',
    features: [
      'Start from templates or custom sizes',
      'Edit text, visuals, and layout in one workspace',
      'Create social and sales creatives faster',
      'Keep all active designs organized',
      'Export polished creatives when ready',
    ],
  },
  'branding-assets': {
    heading: 'Branding / Assets',
    description: 'Keep your media library organized so your team can find and use assets instantly.',
    features: [
      'Central library for brand and campaign files',
      'Folder-based organization for easy access',
      'Upload, manage, and update assets anytime',
      'Clean structure for team collaboration',
    ],
  },
  'academy-training-online': {
    heading: 'Academy / Online Training',
    description: 'Upskill continuously with online sessions that help your team execute better.',
    features: [
      'Upcoming and past sessions in one view',
      'Join live sessions directly',
      'Calendar reminders for training continuity',
      'Practical learning for better on-ground execution',
    ],
  },
  'academy-training-onsite': {
    heading: 'Academy / On Site Training',
    description: 'Build stronger execution through hands-on in-person training and guided sessions.',
    features: [
      'On-site sessions focused on real business scenarios',
      'Structured learning path for franchise teams',
      'Trainer-led practice and implementation support',
      'Consistent skill development on the ground',
    ],
  },
  'academy-study': {
    heading: 'Academy / Study Material',
    description: 'Use structured study resources to improve process clarity and decision-making.',
    features: [
      'Organized learning materials by topic',
      'Reference guides for daily operations',
      'Playbooks and checklists for consistency',
      'Self-paced revision resources',
    ],
  },
  'exclusive-mandates': {
    heading: 'Exclusive Mandates',
    description: 'Explore curated high-value opportunities and register interest directly from your dashboard.',
    features: [
      'Curated mandate opportunities in one stream',
      'Detailed project summaries and highlights',
      'Direct channel partner registration flow',
      'Quick access to premium opportunity information',
    ],
  },
  events: {
    heading: 'Events',
    description: 'Plan participation in key events to improve visibility, relationships, and deal opportunities.',
    features: [
      'Upcoming and past events in one place',
      'Event details with clear next steps',
      'Book your spot quickly from the dashboard',
      'Calendar-friendly planning support',
    ],
  },
  templates: {
    heading: 'Templates',
    description: 'Speed up business execution with ready document templates you can edit and use instantly.',
    features: [
      'Professional templates for agreements, notices, and marketing',
      'Easy customization for your local requirements',
      'Faster document generation for daily workflows',
      'Reusable formats that keep quality consistent',
    ],
  },
  support: {
    heading: 'Support',
    description: 'Get timely help when needed so operations stay smooth and uninterrupted.',
    features: [
      'Help articles for common questions',
      'Simple ticket creation and tracking',
      'Follow-up support in one place',
      'Reliable assistance for operational issues',
    ],
  },
};

const FranchiseDashboardDemoPage = () => {
  const authUser = useContext(AuthUserContext);
  const displayName =
    authUser?.displayName ||
    authUser?.username ||
    (authUser?.email ? authUser.email.split('@')[0] : 'Franchisee');
  const displayEmail = authUser?.email || 'franchisee@douglasmccartney.com';
  const avatarInitial = (displayName || 'U').charAt(0).toUpperCase();
  const [selectedSection, setSelectedSection] = useState('home');
  const [leadsOpen, setLeadsOpen] = useState(true);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [academyOpen, setAcademyOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);

  const openParentForSection = (sectionKey) => {
    if (sectionKey.startsWith('leads-')) {
      setLeadsOpen(true);
    }
    if (sectionKey.startsWith('branding-')) {
      setBrandingOpen(true);
    }
    if (sectionKey.startsWith('academy-')) {
      setAcademyOpen(true);
      if (sectionKey.startsWith('academy-training-')) {
        setTrainingOpen(true);
      }
    }
  };

  const selectSection = (sectionKey) => {
    setSelectedSection(sectionKey);
    openParentForSection(sectionKey);
  };

  const content = SECTION_CONTENT[selectedSection] || SECTION_CONTENT.home;
  const headerTitle = SECTION_LABELS[selectedSection] || 'For Brokers / Franchise';
  const navStyleFor = (sectionKey) =>
    selectedSection === sectionKey ? ACTIVE_NAV_STYLE : INACTIVE_NAV_STYLE;

  return (
    <div className="demo-preview-page">
      <div className="demo-preview-bar">
        <span>This is a preview of the franchise dashboard. This view is non-functional.</span>
        <div className="demo-preview-bar-actions">
          <Link to={DASHBOARD_URL} className="demo-preview-bar-btn">Home</Link>
          <Link to={DASHBOARD_URL} className="demo-preview-bar-btn">Close</Link>
        </div>
      </div>

      <div className="dashboard-container demo-dashboard-preview">
        <aside className="dashboard-sidebar">
          <div className="sidebar-logo">
            <img
              src="/Douglas2.png"
              alt="Douglas & McCartney"
              className="sidebar-logo-img"
            />
          </div>

          <nav className="sidebar-nav">
            <div className="nav-section">
              <button
                type="button"
                onClick={() => selectSection('home')}
                className={`nav-button nav-home-button ${selectedSection === 'home' ? 'active' : ''}`}
              >
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span>Home</span>
              </button>
            </div>

            <div className="nav-section">
              <button
                type="button"
                className={`nav-button ${leadsOpen ? 'active' : ''}`}
                onClick={() => setLeadsOpen((prev) => !prev)}
              >
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Leads</span>
                <svg className={`dropdown-arrow ${leadsOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {leadsOpen && (
                <div className="nav-dropdown">
                  <button
                    type="button"
                    className="nav-item nav-item-button"
                    style={navStyleFor('leads-crm')}
                    onClick={() => selectSection('leads-crm')}
                  >
                    CRM
                  </button>
                  <button
                    type="button"
                    className="nav-item nav-item-button"
                    style={navStyleFor('leads-request')}
                    onClick={() => selectSection('leads-request')}
                  >
                    Request Lead/Listing
                  </button>
                </div>
              )}
            </div>

            <div className="nav-section">
              <button
                type="button"
                className={`nav-button ${brandingOpen ? 'active' : ''}`}
                onClick={() => setBrandingOpen((prev) => !prev)}
              >
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                </svg>
                <span className="nav-label-nowrap">Branding & Marketing Suite</span>
                <svg className={`dropdown-arrow ${brandingOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {brandingOpen && (
                <div className="nav-dropdown">
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('branding-overview')} onClick={() => selectSection('branding-overview')}>Overview</button>
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('branding-brand-kit')} onClick={() => selectSection('branding-brand-kit')}>Brand Kit</button>
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('branding-printables')} onClick={() => selectSection('branding-printables')}>Printables</button>
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('branding-studio')} onClick={() => selectSection('branding-studio')}>Studio (Beta)</button>
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('branding-assets')} onClick={() => selectSection('branding-assets')}>Assets</button>
                </div>
              )}
            </div>

            <div className="nav-section">
              <button
                type="button"
                className={`nav-button ${academyOpen ? 'active' : ''}`}
                onClick={() => setAcademyOpen((prev) => !prev)}
              >
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6l7 3-7 3-7-3 7-3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10v6l7 3 7-3v-6" />
                </svg>
                <span>Academy</span>
                <svg className={`dropdown-arrow ${academyOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {academyOpen && (
                <div className="nav-dropdown">
                  <div className="nav-sub-section">
                    <button
                      type="button"
                      className={`nav-sub-button ${trainingOpen ? 'active' : ''}`}
                      onClick={() => setTrainingOpen((prev) => !prev)}
                    >
                      <span>Training</span>
                      <svg className={`dropdown-arrow ${trainingOpen ? 'open' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {trainingOpen && (
                      <div className="nav-sub-dropdown">
                        <button type="button" className="nav-item nav-item-button" style={navStyleFor('academy-training-online')} onClick={() => selectSection('academy-training-online')}>Online Training</button>
                        <button type="button" className="nav-item nav-item-button" style={navStyleFor('academy-training-onsite')} onClick={() => selectSection('academy-training-onsite')}>On site training</button>
                      </div>
                    )}
                  </div>
                  <button type="button" className="nav-item nav-item-button" style={navStyleFor('academy-study')} onClick={() => selectSection('academy-study')}>Study Material</button>
                </div>
              )}
            </div>

            <div className="nav-section">
              <button type="button" className={`nav-button ${selectedSection === 'exclusive-mandates' ? 'active' : ''}`} onClick={() => selectSection('exclusive-mandates')}>
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                </svg>
                <span>Exclusive Mandates</span>
              </button>
            </div>

            <div className="nav-section">
              <button type="button" className={`nav-button ${selectedSection === 'events' ? 'active' : ''}`} onClick={() => selectSection('events')}>
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10m-12 8h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Events</span>
              </button>
            </div>

            <div className="nav-section">
              <button type="button" className={`nav-button ${selectedSection === 'templates' ? 'active' : ''}`} onClick={() => selectSection('templates')}>
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                </svg>
                <span>Templates</span>
              </button>
            </div>

            <div className="nav-section">
              <button type="button" className={`nav-button ${selectedSection === 'support' ? 'active' : ''}`} onClick={() => selectSection('support')}>
                <svg className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-1.414 1.414M6.343 17.657l-1.414 1.414m13.435 0l-1.414-1.414M6.343 6.343L4.929 4.929m13.435 6.364H20m-4 0a4 4 0 11-8 0 4 4 0 018 0zM4 12h2" />
                </svg>
                <span>Support</span>
              </button>
            </div>
          </nav>

          <div className="sidebar-footer">
            <button type="button" className="sidebar-signout">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </aside>

        <main className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-content">
              <h1 className="header-title">{headerTitle}</h1>
              <div className="header-actions">
                <div className="header-notifications">
                  <button type="button" className="notification-button" title="Notifications">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className="notification-badge">1</span>
                  </button>
                </div>

                <div className="profile-dropdown-container">
                  <button type="button" className="user-info user-info-button">
                    <div className="user-details">
                      <span className="user-name">{displayName}</span>
                      <span className="user-email">{displayEmail}</span>
                    </div>
                    <div className="user-avatar">{avatarInitial}</div>
                  </button>
                </div>
              </div>
            </div>
          </header>

          <div className="dashboard-content demo-preview-content">
            <section className="demo-preview-panel" aria-labelledby="preview-section-heading">
              <span className="demo-preview-panel-eyebrow">What You Actually Get In This Section</span>
              <h2 id="preview-section-heading">{content.heading}</h2>
              <p className="demo-preview-panel-description">{content.description}</p>
              <ul className="demo-preview-feature-list">
                {content.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <div className="demo-preview-action-row">
                <Link to={ACCESS_URL} className="demo-preview-access-btn">
                  Gain Full Access
                </Link>
                <Link to={PRODUCT_URL} className="demo-preview-access-btn demo-preview-access-btn-secondary">
                  View Product Page
                </Link>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FranchiseDashboardDemoPage;
